import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { ISubscriptionBlockApiPort } from '@subscriptions/application/ports';
import type { UpdateBlockInvoiceDataDto, SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { mapSubscriptionBlockToViewDto } from '@subscriptions/application/mappers';
import { extractErrorAttributes } from '@subscriptions/application/utils';

const subLog = logger.forDomain('SUB', { subdomain: 'lifecycle' });
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for updating the invoice data of a subscription block.
 *
 * @param port - Subscription block API port
 * @returns Use case executor
 */
export const updateBlockInvoiceDataUseCase = (port: ISubscriptionBlockApiPort) => {
  const execute = async (blockUuid: string, dto: UpdateBlockInvoiceDataDto): Promise<SubscriptionBlockViewDto> => {
    const span = tracer.startSpan('SUB.updateBlockInvoiceData', {
      attributes: {
        'block.uuid': blockUuid,
        'invoice.uuid': dto.invoiceDataUuid ?? '',
        'invoice.unlinked': dto.invoiceDataUuid === null,
      },
    });
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const block = await port.updateInvoiceData(blockUuid, dto);
        const view = mapSubscriptionBlockToViewDto(block);
        subLog.info('updateBlockInvoiceData', {
          'block.uuid': blockUuid,
          'invoice.uuid': dto.invoiceDataUuid ?? '',
          'invoice.unlinked': dto.invoiceDataUuid === null,
        });
        return view;
      } catch (error) {
        subLog.error('updateBlockInvoiceData', {
          ...extractErrorAttributes(error),
          'block.uuid': blockUuid,
        });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
